/**
 * Modern fintech-themed canvas animation with particles and network effect
 */
export function initFintechAnimation(canvasId = 'fintech-canvas') {
  const canvas = document.getElementById(canvasId);
  if (!canvas) {
    console.warn(`[initFintechAnimation] Canvas #${canvasId} not found`);
    return () => {};
  }

  const ctx = canvas.getContext('2d');
  let animationId = null;
  let particles = [];
  let mouse = { x: null, y: null, radius: 150 };

  // Set canvas size
  function resizeCanvas() {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }
  resizeCanvas();

  // Particle class
  class Particle {
    constructor() {
      this.x = Math.random() * canvas.width;
      this.y = Math.random() * canvas.height;
      this.size = Math.random() * 3 + 1;
      this.speedX = Math.random() * 2 - 1;
      this.speedY = Math.random() * 2 - 1;
      this.color = '#3ebea9';
      this.opacity = Math.random() * 0.5 + 0.5;
      this.pulsePhase = Math.random() * Math.PI * 2;
    }

    update() {
      // Movement
      this.x += this.speedX;
      this.y += this.speedY;

      // Bounce off walls
      if (this.x > canvas.width || this.x < 0) {
        this.speedX = -this.speedX;
      }
      if (this.y > canvas.height || this.y < 0) {
        this.speedY = -this.speedY;
      }

      // Mouse repulsion
      if (mouse.x != null && mouse.y != null) {
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < mouse.radius) {
          const force = (mouse.radius - distance) / mouse.radius;
          const angle = Math.atan2(dy, dx);
          this.x -= Math.cos(angle) * force * 2;
          this.y -= Math.sin(angle) * force * 2;
        }
      }

      // Pulse effect
      this.pulsePhase += 0.02;
      this.size = Math.sin(this.pulsePhase) * 0.5 + 2;
    }

    draw() {
      ctx.save();
      ctx.globalAlpha = this.opacity;
      
      // Glow effect
      ctx.shadowBlur = 10;
      ctx.shadowColor = this.color;
      
      // Draw particle
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fill();
      
      // Inner bright core
      ctx.globalAlpha = this.opacity * 0.5;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size * 0.5, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.restore();
    }
  }

  // Create particles
  function createParticles() {
    const particleCount = Math.floor((canvas.width * canvas.height) / 15000);
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }
  }

  // Draw connections between nearby particles
  function drawConnections() {
    const maxDistance = 150;
    
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x;
        const dy = particles[i].y - particles[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < maxDistance) {
          const opacity = (1 - distance / maxDistance) * 0.3;
          
          ctx.save();
          ctx.globalAlpha = opacity;
          ctx.strokeStyle = '#3ebea9';
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(particles[j].x, particles[j].y);
          ctx.stroke();
          ctx.restore();
        }
      }
    }
  }

  // Draw data flow lines (animated)
  let flowPhase = 0;
  function drawDataFlow() {
    flowPhase += 0.01;
    
    // Create flowing data lines
    ctx.save();
    ctx.globalAlpha = 0.1;
    ctx.strokeStyle = '#3ebea9';
    ctx.lineWidth = 1;
    ctx.setLineDash([10, 10]);
    ctx.lineDashOffset = -flowPhase * 50;
    
    // Draw some curved paths
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      const startX = -100;
      const startY = canvas.height * (0.2 + i * 0.3);
      const endX = canvas.width + 100;
      const endY = canvas.height * (0.3 + i * 0.3);
      const cpX = canvas.width / 2;
      const cpY = startY + Math.sin(flowPhase + i) * 100;
      
      ctx.moveTo(startX, startY);
      ctx.quadraticCurveTo(cpX, cpY, endX, endY);
      ctx.stroke();
    }
    
    ctx.restore();
  }

  // Main animation loop
  function animate() {
    // Clear canvas with fade effect
    ctx.fillStyle = 'rgba(10, 14, 39, 0.05)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw background grid
    drawGrid();
    
    // Draw data flow
    drawDataFlow();
    
    // Update and draw particles
    particles.forEach(particle => {
      particle.update();
      particle.draw();
    });
    
    // Draw connections
    drawConnections();
    
    animationId = requestAnimationFrame(animate);
  }

  // Draw subtle grid
  function drawGrid() {
    ctx.save();
    ctx.globalAlpha = 0.03;
    ctx.strokeStyle = '#3ebea9';
    ctx.lineWidth = 0.5;
    
    const gridSize = 50;
    
    for (let x = 0; x < canvas.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    
    for (let y = 0; y < canvas.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
    
    ctx.restore();
  }

  // Mouse tracking
  function handleMouseMove(e) {
    mouse.x = e.clientX;
    mouse.y = e.clientY;
  }

  function handleMouseLeave() {
    mouse.x = null;
    mouse.y = null;
  }

  // Event listeners
  window.addEventListener('resize', () => {
    resizeCanvas();
    particles = [];
    createParticles();
  });
  
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseleave', handleMouseLeave);

  // Initialize
  createParticles();
  animate();

  // Cleanup function
  return () => {
    cancelAnimationFrame(animationId);
    window.removeEventListener('resize', resizeCanvas);
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseleave', handleMouseLeave);
    particles = [];
  };
}

// Legacy support for old animation function name
export function initAnimation(elId) {
  return initFintechAnimation(elId === 'mapping' ? 'fintech-canvas' : elId);
}